import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router'
import { ArrowLeft } from 'lucide-react'
import { supabase } from '@/lib/supabase'
import { useQuery } from '@tanstack/react-query'
import { Page, PageHeader, btnSecondary, EmptyState } from '@/components/rm'
import { SwmsEditor } from '@/components/swms/swms-editor'
import { useSwms, useJobHrcw } from '@/hooks/use-swms'
import { FEATURES } from '@/lib/features'

// MySerbisyo (spec 001): SWMS is off, so the page sends the person back to the job.
export const Route = createFileRoute('/_app/jobs_/$id_/swms')({
  beforeLoad: ({ params }) => {
    if (!FEATURES.swms) throw redirect({ to: '/jobs/$id', params: { id: params.id }, replace: true })
  },
  component: JobSwmsPage,
})

/**
 * The SWMS gets its own page rather than a modal. It is a five-column document that a crew reads
 * on a phone at the start of a shift, and squeezing it into a dialog would have cost the column
 * the regulators care most about — "how will the controls be implemented" — its width.
 */
function JobSwmsPage() {
  const { id } = Route.useParams()
  const jobId = Number(id)
  const navigate = useNavigate()

  const { data: job } = useQuery({
    queryKey: ['job-swms-header', jobId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('task').select('id, name, status, hrcw_categories').eq('id', jobId).maybeSingle()
      if (error) throw error
      return data as { id: number; name: string | null; status: string | null; hrcw_categories: string[] | null } | null
    },
  })

  const { jurisdiction } = useJobHrcw(job?.hrcw_categories)
  const { current, isLoading } = useSwms(jobId)

  const back = () => navigate({ to: '/jobs/$id', params: { id } })

  return (
    <Page>
      <PageHeader title={job?.name ? `SWMS — ${job.name}` : 'SWMS'} />
      <button className={btnSecondary + ' mb-4'} onClick={back}>
        <ArrowLeft className="h-4 w-4" /> Back to the job
      </button>

      {isLoading ? (
        <div className="h-6 w-40 animate-pulse rounded bg-canvas-subtle" />
      ) : current ? (
        <SwmsEditor record={current} jurisdiction={jurisdiction} onClose={back} />
      ) : (
        <EmptyState label="No SWMS on this job yet. Start one from the Safety strip on the job." cta="Back to the job" onCta={back} />
      )}
    </Page>
  )
}
